/**
 * FeedbackWidget — global always-on "Give us feedback" affordance.
 *
 * Mounted once in App.js under AuthProvider. Renders ONLY when the user is
 * authenticated (traders, brokers, admins). Free-text field + kind selector
 * (feature / bug / general). Auto-captures the current pathname so admins
 * know where the user hit the issue.
 *
 * Positioning: bottom-right, ABOVE the CookiePreferencesLauncher. Sept 2026:
 * the chat bubbles that used to sit below these two were removed, so this
 * is now just a 2-item stack. Uses a compact pill collapsed → expands into
 * a card on click. Never covers primary content because we live in the
 * corner strip.
 *
 * The submission goes to POST /api/feedback which also fires an in-app
 * notification into the super-admin's inbox.
 */
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquarePlus, X, Bug, Lightbulb, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KINDS = [
    { k: "feature", label: "Feature idea",   icon: Lightbulb,       tone: "text-emerald-500 border-emerald-500/50 bg-emerald-500/10" },
    { k: "bug",     label: "Something broke", icon: Bug,             tone: "text-red-500 border-red-500/50 bg-red-500/10" },
    { k: "general", label: "General",         icon: MessageCircle,   tone: "text-primary border-primary/50 bg-primary/10" },
];

export default function FeedbackWidget() {
    const { user } = useAuth();
    const location = useLocation();
    const [open, setOpen]     = useState(false);
    const [kind, setKind]     = useState("general");
    const [text, setText]     = useState("");
    const [busy, setBusy]     = useState(false);

    // Only render for authenticated users — that's the entire target audience
    // the product spec called out (trader/broker/admin dashboards).
    if (!user) return null;

    const submit = async (e) => {
        e.preventDefault();
        const trimmed = text.trim();
        if (trimmed.length < 3) {
            toast.error("Say a little more (3+ characters)");
            return;
        }
        setBusy(true);
        try {
            await api.post("/feedback", {
                kind,
                message:   trimmed,
                page_path: location.pathname + (location.search || ""),
                user_agent: navigator.userAgent?.slice(0, 400) || null,
            });
            toast.success("Thanks — your feedback is with our team");
            setText("");
            setKind("general");
            setOpen(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send — try again");
        } finally {
            setBusy(false);
        }
    };

    if (!open) {
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label="Send feedback"
                data-testid="feedback-launcher"
                className="
                    fixed right-5 bottom-32 md:bottom-16 z-[9997]
                    inline-flex items-center gap-1.5
                    px-3 py-2 rounded-full
                    border border-primary/40 bg-primary/95 text-primary-foreground
                    text-xs font-bold shadow-lg
                    hover:brightness-110 hover:scale-[1.02] transition
                "
            >
                <MessageSquarePlus className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">Feedback</span>
            </button>
        );
    }

    return (
        <div
            role="dialog"
            aria-label="Send feedback"
            data-testid="feedback-panel"
            className="
                fixed right-5 bottom-32 md:bottom-16 z-[9997]
                w-[92vw] max-w-[380px]
                rounded-xl border border-border bg-card shadow-2xl
                p-4 space-y-3
            "
        >
            <div className="flex items-start justify-between gap-2">
                <div>
                    <div className="overline text-primary">Tell the team</div>
                    <div className="font-extrabold text-sm">Send feedback</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        Feature ideas, bugs, or general notes — goes straight to super-admin.
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Close feedback"
                    data-testid="feedback-close"
                ><X className="w-4 h-4" /></button>
            </div>

            <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-3 gap-1.5" data-testid="feedback-kind-picker">
                    {KINDS.map((o) => {
                        const active = kind === o.k;
                        const Icon = o.icon;
                        return (
                            <button
                                key={o.k}
                                type="button"
                                onClick={() => setKind(o.k)}
                                data-testid={`feedback-kind-${o.k}`}
                                className={`flex flex-col items-center gap-1 rounded-md border px-2 py-2 text-[10px] font-bold uppercase tracking-widest transition ${
                                    active ? o.tone : "border-border text-muted-foreground hover:border-primary/30"
                                }`}
                            >
                                <Icon className="w-4 h-4" />
                                {o.label}
                            </button>
                        );
                    })}
                </div>

                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="What's on your mind? The more detail the better."
                    rows={4}
                    maxLength={4000}
                    autoFocus
                    data-testid="feedback-textarea"
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded focus:border-primary focus:outline-none resize-y"
                />
                <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                    <span className="font-mono truncate max-w-[70%]" title={location.pathname}>
                        page: {location.pathname}
                    </span>
                    <span className={text.length > 3500 ? "text-amber-500 font-bold" : ""}>
                        {text.length}/4000
                    </span>
                </div>

                <button
                    type="submit"
                    disabled={busy || text.trim().length < 3}
                    data-testid="feedback-submit"
                    className="w-full py-2 rounded bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
                >
                    <Send className="w-4 h-4" />
                    {busy ? "Sending…" : "Send feedback"}
                </button>
            </form>
        </div>
    );
}
